import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface ImpersonatedUser {
  id: string;
  name: string;
  email: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  startImpersonation: (userId: string, userName: string, userEmail: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  getEffectiveUserId: () => Promise<string | null>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'impersonation_data';
const IMPERSONATION_LOG_KEY = 'impersonation_log_id';

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);

  // Load impersonation state from sessionStorage on mount and verify it's still valid
  useEffect(() => {
    const storedData = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    const logId = sessionStorage.getItem(IMPERSONATION_LOG_KEY);
    
    if (storedData && logId) {
      try {
        const data = JSON.parse(storedData);
        
        // IMMEDIATELY restore state synchronously so dashboard queries work correctly
        setImpersonatedUser(data);
        setIsImpersonating(true);
        
        // Then verify the session asynchronously in the background
        const verifySession = async () => {
          try {
            const { data: logEntry, error } = await supabase
              .from('impersonation_logs')
              .select('id, ended_at')
              .eq('id', logId)
              .is('ended_at', null)
              .maybeSingle();
            
            if (error) {
              // If table doesn't exist or RLS error, just clean up silently
              if (error.code === 'PGRST116' || error.code === '42P01') {
                // Table doesn't exist - clean up
                sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
                sessionStorage.removeItem(IMPERSONATION_LOG_KEY);
                setImpersonatedUser(null);
                setIsImpersonating(false);
                return;
              }
              throw error;
            }
            
            if (!logEntry) {
              // Session not found or has ended - clean up
              logger.debug('Impersonation session no longer valid, cleaning up');
              sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
              sessionStorage.removeItem(IMPERSONATION_LOG_KEY);
              setImpersonatedUser(null);
              setIsImpersonating(false);
            }
          } catch (error) {
            logger.error('Failed to verify impersonation session:', error);
            // Don't clean up on error - could be network issue
            // But if it's a table missing error, clean up
            if (error && typeof error === 'object' && 'code' in error) {
              const err = error as { code: string };
              if (err.code === 'PGRST116' || err.code === '42P01') {
                sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
                sessionStorage.removeItem(IMPERSONATION_LOG_KEY);
                setImpersonatedUser(null);
                setIsImpersonating(false);
              }
            }
          }
        };
        
        verifySession();
      } catch (error) {
        logger.error('Failed to parse impersonation data:', error);
        sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        sessionStorage.removeItem(IMPERSONATION_LOG_KEY);
      }
    }
  }, []);

  const startImpersonation = async (userId: string, userName: string, userEmail: string) => {
    try {
      // Get the current admin user
      const { data: { user: adminUser } } = await supabase.auth.getUser();
      if (!adminUser) {
        throw new Error('Not authenticated');
      }

      // Verify admin privileges
      if (adminUser.email !== 'admin@northreports.com') {
        throw new Error('Only system admin can impersonate users');
      }

      // Don't allow impersonating yourself or other admins
      if (userId === adminUser.id || userEmail === 'admin@northreports.com') {
        throw new Error('Cannot impersonate admin users');
      }

      // End any previous impersonation sessions for this admin (cleanup)
      await supabase
        .from('impersonation_logs')
        .update({ ended_at: new Date().toISOString() })
        .eq('admin_user_id', adminUser.id)
        .is('ended_at', null);

      // Create impersonation log entry
      const { data: logEntry, error: logError } = await supabase
        .from('impersonation_logs')
        .insert({
          admin_user_id: adminUser.id,
          impersonated_user_id: userId,
          started_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (logError) {
        logger.error('Failed to create impersonation log:', logError);
        throw logError;
      }

      // Store impersonation data
      const impersonationData = {
        id: userId,
        name: userName,
        email: userEmail,
      };

      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationData));
      sessionStorage.setItem(IMPERSONATION_LOG_KEY, logEntry.id);

      setImpersonatedUser(impersonationData);
      setIsImpersonating(true);
    } catch (error) {
      logger.error('Failed to start impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const logId = sessionStorage.getItem(IMPERSONATION_LOG_KEY);

      if (logId) {
        // Update the log entry with end time
        await supabase
          .from('impersonation_logs')
          .update({ ended_at: new Date().toISOString() })
          .eq('id', logId);
      }

      // Clear session storage
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      sessionStorage.removeItem(IMPERSONATION_LOG_KEY);

      setImpersonatedUser(null);
      setIsImpersonating(false);
    } catch (error) {
      logger.error('Failed to stop impersonation:', error);
      throw error;
    }
  };

  const getEffectiveUserId = async (): Promise<string | null> => {
    try {
    // If impersonating, return the impersonated user's ID
    if (isImpersonating && impersonatedUser) {
      return impersonatedUser.id;
    }

    // Otherwise, return the real authenticated user's ID
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        logger.error('Error getting user from Supabase auth:', error);
        throw error;
      }
    return user?.id || null;
    } catch (error: any) {
      logger.error('Failed to get effective user ID:', error);
      // If it's a network error, provide a more helpful message
      const errorMessage = error?.message || '';
      const errorName = error?.name || '';
      if (
        errorName === 'TypeError' ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('ERR_CONNECTION') ||
        errorMessage.includes('network') ||
        errorMessage.includes('NetworkError')
      ) {
        throw new Error('Connection to server failed. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        impersonatedUser,
        startImpersonation,
        stopImpersonation,
        getEffectiveUserId,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
